import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Chip,
  Button,
} from '../../components';
import { formatRelativeTime, getStatusLabel, getStatusChipVariant, getGradeColor } from '../../utils';
import type { InspectionStatus, UUID } from '../../types/api';

// Mock history storage (in real app, this would come from localStorage or backend)
const HISTORY_STORAGE_KEY = 'car-ia-inspection-history';

interface HistoryItem {
  inspectionId: UUID;
  plate: string;
  createdAt: string;
  status: InspectionStatus;
  grade?: 'A' | 'B' | 'C';
  score?: number;
  thumbnailUrl?: string;
}

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Load history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        setHistory(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save to localStorage
  const saveHistory = (newHistory: HistoryItem[]) => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(newHistory));
      setHistory(newHistory);
    } catch (error) {
      console.error('Failed to save history:', error);
    }
  };

  const handleInspectionClick = (item: HistoryItem) => {
    if (item.status === 'processing') {
      navigate(`/inspecao/status/${item.inspectionId}`, { replace: true });
    } else if (item.status === 'done') {
      navigate(`/inspecao/status/${item.inspectionId}`, { replace: true });
    } else {
      // Failed - could offer retry
      navigate(`/inspecao/status/${item.inspectionId}`, { replace: true });
    }
  };

  const handleDelete = (inspectionId: UUID, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Remover esta vistoria do histórico?')) {
      saveHistory(history.filter(h => h.inspectionId !== inspectionId));
    }
  };

  const handleClearAll = () => {
    if (history.length > 0 && confirm('Limpar todo o histórico?')) {
      saveHistory([]);
    }
  };

  if (loading) {
    return (
      <Page appBar={{ title: 'Histórico', elevation: false, transparent: true }}>
        <div className="flex items-center justify-center flex-1">
          <span className="material-symbols-outlined animate-spin-slow text-primary text-3xl">sync</span>
        </div>
      </Page>
    );
  }

  return (
    <Page
      appBar={{
        title: 'Minhas Vistorias',
        elevation: false,
        transparent: true,
        actions: history.length > 0 ? [
          <Button variant="ghost" size="sm" onClick={handleClearAll} className="mr-2">
            <span className="material-symbols-outlined text-sm">delete_sweep</span>
            Limpar
          </Button>
        ] : []
      }}
      className="pb-24"
    >
      {history.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 text-center px-4">
          <div className="w-24 h-24 rounded-full bg-surface-container-high flex items-center justify-center mx-auto mb-6">
            <span className="material-symbols-outlined text-on-surface-variant text-4xl">history</span>
          </div>
          <h2 className="font-headline-lg">Nenhuma vistoria ainda</h2>
          <p className="font-body-md text-on-surface-variant mt-2 max-w-sm">
            Sua primeira vistoria aparecerá aqui após o envio.
          </p>
          <Button
            variant="primary"
            className="mt-6"
            onClick={() => navigate('/inspecao/nova')}
            leftIcon={<span className="material-symbols-outlined">add_circle</span>}
          >
            Fazer Primeira Vistoria
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <Card
              key={item.inspectionId}
              variant="interactive"
              padding="md"
              onClick={() => handleInspectionClick(item)}
            >
              <div className="flex items-center gap-4">
                {/* Thumbnail */}
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-container flex-shrink-0 relative">
                  {item.thumbnailUrl ? (
                    <img src={item.thumbnailUrl} alt={item.plate} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-on-surface-variant">directions_car</span>
                    </div>
                  )}
                  <Chip
                    variant={getStatusChipVariant(item.status)}
                    size="sm"
                    className="absolute bottom-1 right-1"
                  >
                    {getStatusLabel(item.status)}
                  </Chip>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-headline-sm truncate">{item.plate}</h3>
                    {item.grade && (
                      <Chip
                        variant={getGradeColor(item.grade) as any}
                        size="sm"
                        icon={<span className="material-symbols-outlined text-xs">grade</span>}
                      >
                        Nota {item.grade}
                      </Chip>
                    )}
                  </div>
                  <p className="font-body-sm text-on-surface-variant mt-1">
                    {formatRelativeTime(item.createdAt)}
                  </p>
                  {item.score !== undefined && (
                    <p className="font-body-sm text-on-surface-variant">
                      Score: <span className="font-label-md tabular-nums">{item.score}</span>/100
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col items-end gap-2">
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={(e) => handleDelete(item.inspectionId, e)}
                    className="text-on-surface-variant"
                  >
                    <span className="material-symbols-outlined">more_vert</span>
                  </Button>
                  <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
};

// Export function to add to history from other pages
export function addInspectionToHistory(item: Omit<HistoryItem, 'createdAt'> & { createdAt?: string }) {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    const history: HistoryItem[] = stored ? JSON.parse(stored) : [];
    const newItem: HistoryItem = {
      ...item,
      createdAt: item.createdAt || new Date().toISOString(),
    };
    const newHistory = [newItem, ...history.filter(h => h.inspectionId !== newItem.inspectionId)];
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(newHistory));
  } catch (error) {
    console.error('Failed to add to history:', error);
  }
}